import React, { useEffect, useMemo, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Phone, CheckCircle2, AlertCircle, Building2, Smartphone, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { OnboardingFormData } from '../../types';
import twilioService, { TwilioAvailableNumber, TwilioNumberType } from '../../services/twilioService';
import AgentNumberManager, { useAgentNumbers } from '../agents/AgentNumberManager';
import RegulatoryBundleForm from './RegulatoryBundleForm';
import WhatsAppConnectionCard from '../WhatsAppConnectionCard';

type NumberCountry = 'DE' | 'CH' | 'AT';

const COUNTRY_OPTIONS: { value: NumberCountry; label: string }[] = [
  { value: 'DE', label: '🇩🇪 Deutschland' },
  { value: 'CH', label: '🇨🇭 Schweiz' },
  { value: 'AT', label: '🇦🇹 Österreich' },
];

// AT nennt bewusst keine Stadt: österreichische Festnetznummern müssen im
// Ortsnetz der Salon-Adresse liegen, die Suche folgt daher dem Salon-Standort.
const LOCAL_INFO: Record<NumberCountry, { label: string; sub: string }> = {
  DE: { label: 'Festnetz Wiesbaden', sub: '+49 611… (Wiesbaden)' },
  CH: { label: 'Festnetz Bern', sub: '+41 31… (Bern)' },
  AT: { label: 'Festnetz (eigenes Ortsnetz)', sub: '+43 … (Ortsnetz der Salon-Adresse)' },
};

// Map the free-text country from the company step (step 2) to a supported market.
const mapOnboardingCountry = (raw?: string): NumberCountry => {
  const s = (raw || '').trim().toLowerCase();
  if (s.startsWith('schweiz') || s.includes('switzerland') || s === 'ch') return 'CH';
  if (s.startsWith('öster') || s.startsWith('oster') || s.includes('austria') || s === 'at') return 'AT';
  return 'DE';
};

interface TwilioNumberStepProps {
  formData: OnboardingFormData;
  onFormChange: (data: Partial<OnboardingFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const TwilioNumberStep: React.FC<TwilioNumberStepProps> = ({ formData, onFormChange, onNext, onBack }) => {
  const [availableNumbers, setAvailableNumbers] = useState<TwilioAvailableNumber[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
  const [isNumberLoading, setIsNumberLoading] = useState(false);
  const [isLockedToTwilio, setIsLockedToTwilio] = useState(false);
  const [numberType, setNumberType] = useState<TwilioNumberType>('local');
  // Default the market from the company country entered in step 2.
  const initialCountry = mapOnboardingCountry(formData.country);
  const [country, setCountry] = useState<NumberCountry>(initialCountry);
  // DE uses the central TimeGlobe bundle (always "approved"); CH/AT require the
  // customer's own bundle to be twilio-approved before numbers can be searched.
  const [regulatoryApproved, setRegulatoryApproved] = useState(initialCountry === 'DE');
  // Multi-location tenants: assign this number to a subset of their salons so
  // the agent behind it only serves (and only knows) those locations.
  // Agent numbers + salons, shared with the company profile.
  const agentNumbers = useAgentNumbers();

  const [assignedSiteCds, setAssignedSiteCds] = useState<string[]>([]);
  const [agentDisplayName, setAgentDisplayName] = useState('');
  const [assignmentSaved, setAssignmentSaved] = useState(false);

  const needsRegulatory = country !== 'DE';
  const canSearch = !needsRegulatory || regulatoryApproved;

  const wantsPhoneAgent = formData.wantsPhoneAgent;
  const whatsappUseReservedNumber = formData.whatsappUseReservedNumber;
  const needsTwilioNumber = wantsPhoneAgent === true || whatsappUseReservedNumber === true;

  const canContinue = useMemo(() => {
    if (wantsPhoneAgent === undefined) return false;
    if (wantsPhoneAgent) {
      return Boolean(formData.twilioPurchasedNumber?.trim());
    }
    if (whatsappUseReservedNumber === undefined) return false;
    if (whatsappUseReservedNumber) {
      return Boolean(formData.twilioPurchasedNumber?.trim());
    }
    return true;
  }, [wantsPhoneAgent, whatsappUseReservedNumber, formData.twilioPurchasedNumber]);

  const handleNumberTypeChange = (type: TwilioNumberType) => {
    setNumberType(type);
    setAvailableNumbers([]);
    onFormChange({ twilioSelectedNumber: '' });
    // CH/AT-Bundles gelten pro Nummerntyp (local vs. mobile sind getrennte
    // Twilio-Regulierungen). Neu gaten — das Formular ruft selbst onApproved,
    // wenn das Bundle für den gewählten Typ bereits freigegeben ist.
    setRegulatoryApproved(country === 'DE');
  };

  const handleCountryChange = (next: NumberCountry) => {
    setCountry(next);
    setRegulatoryApproved(next === 'DE');  // DE is always ready; CH/AT gated on bundle
    setAvailableNumbers([]);
    onFormChange({ twilioSelectedNumber: '' });
  };

  const handleSearchNumbers = async () => {
    try {
      setIsSearching(true);
      setStatus(null);
      const response = await twilioService.listAvailableNumbers({
        number_type: numberType,
        country,
        limit: 15
      });
      setAvailableNumbers(response.numbers || []);
      if (!response.numbers?.length) {
        setStatus({
          type: 'info',
          message: response.hint || `Keine ${numberType === 'local' ? 'Festnetz' : 'Mobil'}-Nummern verfügbar.`
        });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Fehler beim Laden der verfügbaren Nummern.' });
    } finally {
      setIsSearching(false);
    }
  };

  const handlePurchaseNumber = async () => {
    if (!formData.twilioSelectedNumber) {
      setStatus({ type: 'error', message: 'Bitte wählen Sie eine Nummer aus.' });
      return;
    }
    try {
      setIsPurchasing(true);
      setStatus(null);
      // AT-Fallback: die Liste kann österreichweite (+43 720, national) Nummern
      // enthalten — der Kauf muss dann mit deren Typ laufen (Bundle pro Typ).
      const selected = availableNumbers.find(
        (n) => n.phone_number === formData.twilioSelectedNumber
      );
      const purchaseType = (selected?.number_type as 'local' | 'mobile' | 'national' | undefined) || numberType;
      const response = await twilioService.purchaseNumber({
        phone_number: formData.twilioSelectedNumber,
        number_type: purchaseType
      });
      onFormChange({
        twilioPurchasedNumber: response.phone_number,
        twilioNumberType: numberType,
        wantsTwilioNumber: true,
      });
      setStatus({ type: 'success', message: `Nummer erfolgreich reserviert: ${response.phone_number}` });
    } catch (error) {
      const detail = error instanceof Error && error.message && error.message !== 'Failed to fetch'
        ? error.message
        : null;
      setStatus({ type: 'error', message: detail || 'Nummer konnte nicht gekauft werden.' });
    } finally {
      setIsPurchasing(false);
    }
  };

  const loadLatestNumber = async () => {
    if (formData.twilioPurchasedNumber) {
      setIsLockedToTwilio(true);
      setIsNumberLoading(false);
      return;
    }
    try {
      const response = await twilioService.getLatestNumber();
      if (response.phone_number) {
        onFormChange({
          twilioPurchasedNumber: response.phone_number,
          wantsTwilioNumber: true,
          hasOwnWhatsappNumber: false,
        });
        setIsLockedToTwilio(true);
      }
    } catch (error) {
      // no-op
    } finally {
      setIsNumberLoading(false);
    }
  };

  useEffect(() => {
    if (needsTwilioNumber && !formData.twilioPurchasedNumber) {
      setIsNumberLoading(true);
      loadLatestNumber();
    } else if (formData.twilioPurchasedNumber) {
      setIsLockedToTwilio(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsTwilioNumber]);

  useEffect(() => {
    if (formData.twilioPurchasedNumber) {
      setIsLockedToTwilio(true);
    }
  }, [formData.twilioPurchasedNumber]);

  // Verfügbare Nummern automatisch laden, sobald der Nutzer die Nummernauswahl sieht
  useEffect(() => {
    if (needsTwilioNumber && !isLockedToTwilio && !isNumberLoading && canSearch) {
      handleSearchNumbers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsTwilioNumber, isLockedToTwilio, isNumberLoading, numberType, country, canSearch]);

  const handleSaveAndContinue = async () => {
    if (!canContinue) {
      if (wantsPhoneAgent === undefined) {
        setStatus({ type: 'error', message: 'Bitte beantworte die Frage zum Telefonagenten.' });
        return;
      }
      if (wantsPhoneAgent === false && whatsappUseReservedNumber === undefined) {
        setStatus({ type: 'error', message: 'Bitte wähle, ob WhatsApp mit deiner eigenen Nummer oder einer Nummer von uns verbunden werden soll.' });
        return;
      }
      if (needsTwilioNumber && !formData.twilioPurchasedNumber) {
        setStatus({ type: 'error', message: 'Bitte wählen Sie eine Nummer aus und klicken Sie auf „Nummer reservieren“.' });
        return;
      }
      setStatus({ type: 'error', message: 'Bitte beantworte die Frage und ggf. wähle eine Nummer.' });
      return;
    }
    try {
      setIsSaving(true);
      setStatus(null);
      await twilioService.setOnboardingChoice({
        wants_twilio_number: !!formData.twilioPurchasedNumber,
        has_own_number: false,
      });
      onNext();
    } catch (error) {
      setStatus({ type: 'error', message: 'Auswahl konnte nicht gespeichert werden.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Wenn WhatsApp bereits verbunden ist: Nummer + "Verknüpfung aufheben". */}
      <WhatsAppConnectionCard />

      <div className="space-y-2">
        <h2 className="flex items-center text-xl font-semibold text-foreground">
          <Phone className="mr-2 h-6 w-6 text-[#1967D2]" />
          Telefon-Agent
        </h2>
        <p className="text-muted-foreground text-sm">
          Die Nummer kann für den Telefonagenten und für WhatsApp genutzt werden. Die WhatsApp-Verknüpfung erfolgt im nächsten Schritt.
        </p>
      </div>

      {/* Frage 1: Telefonagent */}
      <Card className="border-[#1967D2]/30">
        <CardHeader>
          <CardTitle className="text-base">Möchten Sie den Telefonagenten nutzen?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Button
              type="button"
              variant={wantsPhoneAgent === true ? "default" : "outline"}
              className={cn(
                "flex-1",
                wantsPhoneAgent === true && "bg-[#1967D2] hover:bg-[#1558b0]"
              )}
              onClick={() => {
                onFormChange({ wantsPhoneAgent: true });
                twilioService.finalizeOnboarding({ wants_phone_agent: true }).catch(() => {});
              }}
            >
              Ja
            </Button>
            <Button
              type="button"
              variant={wantsPhoneAgent === false ? "default" : "outline"}
              className={cn(
                "flex-1",
                wantsPhoneAgent === false && "bg-[#1967D2] hover:bg-[#1558b0]"
              )}
              onClick={() => {
                onFormChange({ wantsPhoneAgent: false });
                twilioService.finalizeOnboarding({ wants_phone_agent: false }).catch(() => {});
              }}
            >
              Nein
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Frage 2: WhatsApp-Nummer (nur wenn Telefonagent = Nein) */}
      {wantsPhoneAgent === false && (
        <Card className="border-[#1967D2]/30">
          <CardHeader>
            <CardTitle className="text-base">Welche Rufnummer soll mit WhatsApp verbunden werden?</CardTitle>
            <CardDescription>
            Wenn Sie bereits eine eigene Rufnummer besitzen, die noch nicht mit WhatsApp Business verknüpft ist, können Sie diese im nächsten Schritt verbinden. Falls keine passende Rufnummer vorhanden ist, ist es erforderlich, eine neue Nummer zu reservieren.            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant={whatsappUseReservedNumber === true ? "default" : "outline"}
                className={cn(
                  "flex-1",
                  whatsappUseReservedNumber === true && "bg-[#1967D2] hover:bg-[#1558b0]"
                )}
                onClick={() => {
                  onFormChange({ whatsappUseReservedNumber: true, hasOwnWhatsappNumber: false });
                  twilioService.finalizeOnboarding({ whatsapp_use_reserved_number: true }).catch(() => {});
                }}
              >
                Neue Nummer reservieren
              </Button>
              <Button
                type="button"
                variant={whatsappUseReservedNumber === false ? "default" : "outline"}
                className={cn(
                  "flex-1",
                  whatsappUseReservedNumber === false && "bg-[#1967D2] hover:bg-[#1558b0]"
                )}
                onClick={() => {
                  onFormChange({ whatsappUseReservedNumber: false, hasOwnWhatsappNumber: true });
                  twilioService.finalizeOnboarding({ whatsapp_use_reserved_number: false }).catch(() => {});
                }}
              >
                Eigene Nummer verwenden
              </Button>
            </div>

            {whatsappUseReservedNumber === false && (
              <Alert className="bg-warning/10 border-warning/25 text-warning-strong">
                <AlertCircle className="h-4 w-4 text-warning-strong" />
                <AlertDescription className="font-medium">
                Ihre eigene Rufnummer muss noch ungenutzt sein und darf derzeit nicht mit WhatsApp oder WhatsApp Business verknüpft sein.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {needsTwilioNumber && (
        <>
          <Alert className="bg-success/10 border-success/25 text-success-strong">
            <CheckCircle2 className="h-4 w-4 text-success-strong" />
            <AlertDescription className="font-medium">
              Alles klar! Dann benötigen Sie eine eigene Nummer{wantsPhoneAgent ? ' für den Telefonagenten. Diese Nummer kann per Rufumleitung an Ihr bestehendes Telefonsystem angeschlossen werden' : ' für WhatsApp'}.
            </AlertDescription>
          </Alert>

          {isNumberLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#1967D2]" />
            </div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Nummer auswählen</CardTitle>
                <CardDescription>
                  Wählen Sie zwischen Festnetz- und Mobilnummer.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!isLockedToTwilio && (
                  <div className="space-y-3">
                    <Label>Land</Label>
                    <div className="grid gap-2 grid-cols-3">
                      {COUNTRY_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleCountryChange(opt.value)}
                          className={cn(
                            "border rounded-lg p-2 text-sm font-medium transition-all",
                            country === opt.value
                              ? "border-[#1967D2] bg-primary/10 text-[#1967D2]"
                              : "border-border hover:border-[#1967D2] text-muted-foreground"
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!isLockedToTwilio && needsRegulatory && !regulatoryApproved && (
                  <RegulatoryBundleForm
                    country={country as 'CH' | 'AT'}
                    numberType={numberType === 'mobile' ? 'mobile' : 'local'}
                    defaults={{
                      businessName: formData.companyName,
                      email: formData.email,
                      street: formData.street,
                      city: formData.city,
                      postalCode: formData.zipCode,
                      vatId: formData.vatId,
                      contactPerson: formData.contactPerson,
                    }}
                    onApproved={() => setRegulatoryApproved(true)}
                  />
                )}

                {!isLockedToTwilio && canSearch && (
                  <>
                    <div className="space-y-3">
                      <Label>Nummerntyp</Label>
                      <div className="grid gap-3 md:grid-cols-2">
                        <button
                          type="button"
                          onClick={() => handleNumberTypeChange('local')}
                          className={cn(
                            "border rounded-lg p-4 text-left transition-all relative",
                            numberType === 'local'
                              ? "border-[#1967D2] bg-primary/10 shadow-sm"
                              : "border-border hover:border-[#1967D2]"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <Building2 className={cn(
                              "h-5 w-5 mt-0.5",
                              numberType === 'local' ? "text-[#1967D2]" : "text-muted-foreground"
                            )} />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-foreground">{LOCAL_INFO[country].label}</span>
                                <span className="text-xs font-semibold text-success-strong bg-success/10 px-2 py-0.5 rounded">
                                  Günstig
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">~1,15€/Monat</p>
                              <p className="text-xs text-muted-foreground/70 mt-1">{LOCAL_INFO[country].sub}</p>
                            </div>
                          </div>
                          {numberType === 'local' && (
                            <CheckCircle2 className="absolute top-2 right-2 h-4 w-4 text-success-strong" />
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleNumberTypeChange('mobile')}
                          className={cn(
                            "border rounded-lg p-4 text-left transition-all relative",
                            numberType === 'mobile'
                              ? "border-[#1967D2] bg-primary/10 shadow-sm"
                              : "border-border hover:border-[#1967D2]"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <Smartphone className={cn(
                              "h-5 w-5 mt-0.5",
                              numberType === 'mobile' ? "text-[#1967D2]" : "text-muted-foreground"
                            )} />
                            <div className="flex-1">
                              <span className="font-medium text-foreground">Mobilnummer</span>
                              <p className="text-sm text-muted-foreground mt-1">~30€/Monat</p>
                              <p className="text-xs text-muted-foreground/70 mt-1">+49 15..., +49 17...</p>
                            </div>
                          </div>
                          {numberType === 'mobile' && (
                            <CheckCircle2 className="absolute top-2 right-2 h-4 w-4 text-success-strong" />
                          )}
                        </button>
                      </div>

                      <Alert className="bg-primary/10 border-primary/25">
                        <Info className="h-4 w-4 text-primary-strong" />
                        <AlertDescription className="text-primary-strong text-sm">
                          {numberType === 'local'
                            ? "Festnetznummern werden per Anruf verifiziert. Der Code wird automatisch erkannt."
                            : "Mobilnummern werden per SMS verifiziert."}
                        </AlertDescription>
                      </Alert>
                    </div>

                    <Button
                      type="button"
                      onClick={handleSearchNumbers}
                      disabled={isSearching}
                      className="mb-3 bg-success text-success-foreground hover:bg-success/90"
                    >
                      {isSearching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {numberType === 'local' ? 'Festnetznummern' : 'Mobilnummern'} aktualisieren
                    </Button>

                    {availableNumbers.length > 0 && (
                      <div className="space-y-2">
                        <Label>Wählen Sie eine Nummer</Label>
                        <div className="grid gap-2 max-h-64 overflow-y-auto">
                          {availableNumbers.map((num) => (
                            <button
                              key={num.phone_number}
                              type="button"
                              onClick={() => onFormChange({ twilioSelectedNumber: num.phone_number })}
                              className={cn(
                                "flex items-center justify-between border rounded-md px-3 py-2 text-sm",
                                formData.twilioSelectedNumber === num.phone_number
                                  ? "border-[#1967D2] bg-primary/10"
                                  : "border-border hover:border-[#1967D2]"
                              )}
                            >
                              <span className="font-medium font-mono">{num.phone_number}</span>
                              <span className="text-muted-foreground text-xs">{num.locality || num.region || 'DE'}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button
                      type="button"
                      onClick={handlePurchaseNumber}
                      disabled={isPurchasing || !formData.twilioSelectedNumber}
                      className="mt-4 bg-[#1967D2] text-white hover:bg-[#1558b0]"
                    >
                      {isPurchasing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Nummer reservieren
                    </Button>
                  </>
                )}

                {formData.twilioPurchasedNumber && (
                  <Alert className="bg-success/10 border-success/25 text-success-strong">
                    <CheckCircle2 className="h-4 w-4 text-success-strong" />
                    <AlertDescription>
                      Ihre Nummer: <strong className="font-mono">{formData.twilioPurchasedNumber}</strong>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Multi-location tenants: scope this number, and add further
                    agent numbers right here instead of sending the customer to
                    the profile after onboarding. */}
                {formData.twilioPurchasedNumber && (
                  <div className="space-y-4 rounded-lg border border-border p-4">
                    <div className="space-y-1">
                      <Label className="text-base font-medium">
                        Welche Salons soll dieser Agent betreuen?
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Anrufer dieser Nummer erreichen einen Agenten, der nur für die gewählten
                        Standorte bucht und berät. Fragt jemand nach einem anderen Standort,
                        verbindet er automatisch zur zuständigen Nummer. Ohne Auswahl gilt:
                        alle Salons.
                      </p>
                    </div>

                    <AgentNumberManager
                      channel="voice"
                      numbers={agentNumbers.numbers}
                      sites={agentNumbers.sites}
                      loading={agentNumbers.loading}
                      drafts={agentNumbers.drafts}
                      onDraftsChange={agentNumbers.setDrafts}
                      showBuy
                      onReload={agentNumbers.reload}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {status && (
        <Alert className={cn(
          status.type === 'success' ? "bg-success/10 border-success/25 text-success-strong" :
          status.type === 'error' ? "bg-destructive/10 border-destructive/30 text-destructive-strong" :
          "bg-primary/10 border-primary/25 text-primary-strong"
        )}>
          {status.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{status.message}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          Zurück
        </Button>
        <Button
          type="button"
          onClick={handleSaveAndContinue}
          disabled={!canContinue || isSaving}
          className="bg-success text-success-foreground hover:bg-success/90"
        >
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Weiter
        </Button>
      </div>
    </div>
  );
};

export default TwilioNumberStep;
